import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar, MapPin, Users, Trophy, Clock, User, MessageCircle, Info, Target, GitBranch } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import PrizesDialog, { CategoryPrizes } from '../PrizesDialog';
import FormatInfoDialog from '../FormatInfoDialog';

interface GameRule {
  label: string;
  value: string;
  icon?: 'target' | 'trophy' | 'bracket' | 'clock' | 'users' | 'calendar';
}

interface LeagueInfoProps {
  leagueName: string;
  startDate: string;
  endDate: string;
  timeRange?: string;
  totalMatches: number;
  matchesPlayed: number;
  sport: 'tennis' | 'padel' | 'pickleball';
  modalidad: 'singles' | 'doubles' | 'dobles';
  partner: string;
  club: string;
  category: string;
  subcategory: string;
  leagueType: 'round-robin' | 'sumatoria-juegos' | 'escalera-piramidal';
  allCategoryPrizes?: CategoryPrizes[];
  userCategory?: string;
  gameRules: GameRule[];
  onContactOrganizer?: () => void;
  organizerName?: string;
  groupRules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additionalRules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
    classification?: {
      firstPlaces?: number;
      rules?: string[];
    };
  };
  gameDays?: string[];
  startTime?: string;
  endTime?: string;
}

const LeagueInfo = ({
  leagueName,
  startDate,
  endDate,
  timeRange,
  totalMatches,
  matchesPlayed,
  sport,
  modalidad,
  partner,
  club,
  category,
  subcategory,
  leagueType,
  allCategoryPrizes = [],
  userCategory,
  gameRules,
  onContactOrganizer,
  groupRules,
  additionalRules,
  gameDays,
  startTime,
  endTime,
  organizerName
}: LeagueInfoProps) => {
  const isMobile = useIsMobile();
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  
  const getSportColor = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'tennis':
        return 'bg-green-100 text-green-800';
      case 'padel':
        return 'bg-emerald-100 text-emerald-800';
      case 'pickleball':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };


  return (
    <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
      <CardContent className={isMobile ? 'p-4' : 'p-5'}>
        {/* Organizador */}
        {organizerName && (
          <div className="flex items-center gap-2 mb-4 text-sm">
            <User className="h-4 w-4 text-primary" />
            <span className="text-muted-foreground">Organizador:</span>
            <span className="font-medium text-foreground">{organizerName}</span>
          </div>
        )}

        {/* 1. Botones de Premios, Reglas y Formato - Grid 3 columnas */}
        <div className={`grid grid-cols-3 ${isMobile ? 'gap-1.5' : 'gap-3'} mb-4`}>
          <PrizesDialog
            eventTitle={leagueName}
            sport={sport}
            prizes={allCategoryPrizes}
            userCategory={userCategory}
          >
            <Button 
              variant="outline" 
              className="w-full h-auto py-3 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 hover:border-yellow-400 hover:from-yellow-100 hover:to-orange-100 transition-all"
            >
              <Trophy className="h-4 w-4 mr-1.5 text-yellow-600" />
              <span className="text-yellow-700 font-medium text-sm">Premios</span>
            </Button>
          </PrizesDialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full h-auto py-3 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 hover:border-primary/50 hover:from-primary/10 hover:to-primary/15 transition-all"
              >
                <Clock className="h-4 w-4 mr-1.5 text-primary" />
                <span className="text-primary font-medium text-sm">Reglas</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Reglas del Juego
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                {gameRules.map((rule, index) => {
                  const getIcon = () => {
                    switch (rule.icon) {
                      case 'target':
                        return <Target className="h-5 w-5 text-primary" />;
                      case 'trophy':
                        return <Trophy className="h-5 w-5 text-yellow-600" />;
                      case 'bracket':
                        return <GitBranch className="h-5 w-5 text-purple-600" />;
                      case 'calendar':
                        return <Clock className="h-5 w-5 text-blue-600" />;
                      default:
                        return <Clock className="h-5 w-5 text-primary" />;
                    }
                  };
                  
                  return (
                    <div 
                      key={index} 
                      className="flex items-start gap-3 p-4 bg-gradient-to-r from-slate-50 to-primary/5 rounded-xl border border-slate-200 shadow-sm"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        {getIcon()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 text-sm">
                          {rule.label}
                        </div>
                        <div className="text-slate-600 text-sm mt-0.5">
                          {rule.value}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>

          {/* Botón de Formato de Liga */}
          <Button 
            variant="outline" 
            onClick={() => setShowFormatDialog(true)}
            className="w-full h-auto py-2 bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-300 hover:border-purple-400 hover:from-purple-100 hover:to-violet-100 transition-all flex flex-col items-center justify-center gap-0.5"
          >
            <div className="flex items-center">
              <Info className="h-4 w-4 mr-1 text-purple-600" />
              <span className="text-purple-700 font-medium text-sm">Formato</span>
            </div>
            <span className="text-purple-600 text-xs">
              {leagueType === 'round-robin' ? 'Round Robin' : 
               leagueType === 'sumatoria-juegos' ? 'Sumatoria' : 'Escalera'}
            </span>
          </Button>
        </div>

        {/* 2. Información compacta */}
        <div className="space-y-3">
          {/* Categoría y Subcategoría */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Categoría:</span>
              <Badge className="bg-purple-100 text-purple-700 border-0">
                {category}
              </Badge>
            </div>
            {subcategory && subcategory.trim() !== '' && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Subcategoría:</span>
                <Badge className="bg-slate-100 text-slate-700 border-0">
                  {subcategory}
                </Badge>
              </div>
            )}
          </div>


          {/* Compañero (si aplica) */}
          {(modalidad === 'doubles' || modalidad === 'dobles') && partner && (
            <div className="flex items-center text-sm text-muted-foreground">
              <User className="h-4 w-4 mr-1.5 text-purple-500" />
              <span>Compañero: <span className="font-medium text-foreground">{partner}</span></span>
            </div>
          )}
        </div>
      </CardContent>
      
      {/* Format Info Dialog */}
      <FormatInfoDialog
        formatType={leagueType}
        formatName={leagueType === 'round-robin' ? 'Round Robin' : 
                    leagueType === 'sumatoria-juegos' ? 'Sumatoria de Juegos' : 'Escalera Piramidal'}
        sport={sport}
        modality={modalidad}
        groupRules={groupRules}
        additionalRules={additionalRules}
        gameDays={gameDays}
        startTime={startTime}
        endTime={endTime}
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
      />
    </Card>
  );
};

export default LeagueInfo;